import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import { Magnetic } from "./Magnetic";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/why", label: "Why You Need A Website" },
  { to: "/work", label: "Examples" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <header className="fixed inset-x-0 top-[3px] z-50">
        <div className="container-x flex items-center justify-between py-4">
          <Link to="/" data-testid="nav-logo" className="font-display text-lg font-bold uppercase tracking-tight text-black mix-blend-difference invert-0">
            <span className="rounded-full bg-white/90 px-4 py-2 shadow-sm backdrop-blur-xl">Mahseri<sup>®</sup></span>
          </Link>

          <nav className="hidden items-center gap-1 rounded-full bg-white/90 p-1.5 shadow-sm backdrop-blur-xl lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-[13px] font-medium transition-colors duration-300 ${
                  pathname === l.to ? "bg-black text-white" : "text-black/70 hover:bg-black/5 hover:text-black"
                }`}
              >
                {l.label}
              </NavLink>
            ))}
            <Magnetic strength={0.2}>
              <Link
                to="/contact"
                data-testid="nav-get-started-btn"
                className="ml-1 rounded-full bg-black px-5 py-2 text-[13px] font-semibold uppercase tracking-wide text-white transition-transform duration-300 hover:scale-105"
              >
                Get Started
              </Link>
            </Magnetic>
          </nav>

          <SheetTrigger asChild><button
            data-testid="nav-hamburger-btn"
            className="relative z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-xl lg:hidden"
            aria-label="Open navigation menu"
          >
            <Menu size={22} />
          </button></SheetTrigger>
        </div>
      </header>

          <SheetContent
            data-testid="mobile-menu-overlay"
            closeButtonProps={{ "data-testid": "mobile-menu-close-button", "aria-label": "Close navigation menu" }}
            aria-describedby={undefined}
            className="dark-section z-[70] flex h-[100dvh] w-full flex-col overflow-y-auto border-0 bg-[#0A0A0A] px-6 pb-8 pt-24 text-white sm:max-w-none"
          >
            <SheetTitle className="sr-only">Mahseri Web navigation</SheetTitle>
            <nav aria-label="Mobile navigation" data-testid="mobile-navigation" className="flex flex-col gap-1">
              {LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    aria-current={pathname === l.to ? "page" : undefined}
                    className={`font-display flex min-h-12 items-center py-2 text-2xl font-semibold tracking-normal transition-colors hover:text-white ${pathname === l.to ? "text-white" : "text-white/65"}`}
                  >
                    {l.label}
                  </Link>
              ))}
            </nav>
            <div className="mt-6 pb-[env(safe-area-inset-bottom)]">
              <Link
                to="/contact"
                data-testid="mobile-nav-get-started-btn"
                onClick={() => setOpen(false)}
                className="inline-block rounded-full bg-white px-8 py-4 font-semibold uppercase tracking-wide text-black"
              >
                Get Started
              </Link>
            </div>
          </SheetContent>
    </Sheet>
  );
}
